/**
 * brollPollAdapter — normalizes B-Roll job status into a PollUpdate.
 *
 * Uses GET /api/v1/jobs/{job_id} (unified job registry).
 * Self-registers on module load so GenerationContext can resume polling
 * persisted B-Roll jobs after a page reload.
 */

import { getApiBaseUrl } from '@/services/urlResolver';
import { getAuthHeadersFromStorage } from '@/utils/httpUtils';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

interface BRollJobData {
  status?: string;
  progress?: number;
  message?: string;
  error?: string;
  started_at?: string | null;
  elapsed_seconds?: number | null;
  estimated_remaining?: number | null;
  extra_metadata?: {
    stage?: string;
    broll_plan?: unknown;
    output_video_url?: string | null;
    error?: string;
  };
}

export interface BRollJobResult {
  outputVideoUrl: string | null;
  brollPlan?: unknown;
}

export const brollPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<BRollJobResult>> => {
  try {
    const baseUrl = getApiBaseUrl();
    const authHeaders = await getAuthHeadersFromStorage();

    const response = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
      headers: authHeaders as Record<string, string>,
    });

    if (!response.ok) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: `Job status request failed (${response.status})`,
          sanitized: sanitizeBackendError({
            error_code: response.status >= 500 ? 'SERVICE_UNAVAILABLE' : undefined,
          }),
        },
      };
    }

    const rawData = (await response.json()) as { success: boolean; data?: BRollJobData };
    const jobData = rawData.data ?? {};

    const timing = {
      startedAt: jobData.started_at ?? null,
      elapsedSeconds: jobData.elapsed_seconds ?? null,
      estimatedRemainingSeconds: jobData.estimated_remaining ?? null,
    };

    const stage = jobData.extra_metadata?.stage ?? jobData.status ?? 'pending';
    const progress = typeof jobData.progress === 'number' ? jobData.progress : undefined;

    // Terminal: failed
    if (jobData.status === 'failed' || jobData.status === 'error') {
      return {
        done: true,
        status: 'failed',
        error: {
          message:
            jobData.extra_metadata?.error ??
            jobData.error ??
            jobData.message ??
            'B-Roll job failed',
          sanitized: sanitizeBackendError(jobData),
        },
        timing,
      };
    }

    // Special: awaiting_review — not terminal, but we stop progressing
    if (stage === 'awaiting_review') {
      return {
        done: false,
        status: 'running',
        progress,
        stage: 'awaiting_review',
        message: jobData.message ?? 'Awaiting review',
        timing,
      };
    }

    // Terminal: completed
    if (jobData.status === 'completed') {
      return {
        done: true,
        status: 'completed',
        progress: 100,
        stage: 'completed',
        result: {
          outputVideoUrl: jobData.extra_metadata?.output_video_url ?? null,
          brollPlan: jobData.extra_metadata?.broll_plan,
        },
        timing,
      };
    }

    // Non-terminal: still processing
    return {
      done: false,
      status: 'running',
      progress,
      stage,
      message: jobData.message ?? undefined,
      timing,
    };
  } catch (err) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: err instanceof Error ? err.message : 'Network error polling B-Roll job',
        sanitized: sanitizeBackendError(null),
      },
    };
  }
};

registerPollAdapter('broll-upload', brollPollAdapter);
